import { createTransformers } from './create-transformers';

export const transformers = createTransformers({
  // FILTERABLE DIMENSION ATTRIBUTES
  /**
   * Filterable dimension attributes are those that are used to filter against an endpoint. You can map each
   * endpoint to a single `dimension.attribute` path in the request.
   */
  filterableMap: {
    // PRODUCT ENDPOINTS
    '/core/products': 'product.products',
    '/core/products/departments': 'product.departments',
    '/core/products/sub-departments': 'product.sub_departments',
    '/core/products/seasons': 'product.seasons',
    '/core/products/collection-types': 'product.collection_types',
    '/core/products/events': 'product.events',
    '/core/products/brands': 'product.brands',
    '/core/products/season-events': 'product.season_events',
    '/core/products/budget-levels': 'product.budget_levels',
    '/core/products/classes': 'product.classes',
    '/core/products/sub-classes': 'product.sub_classes',
    '/core/products/manufacturers': 'product.manufacturers',
    '/core/products/articles': 'product.articles',
    '/core/products/materials': 'product.materials',
    '/core/products/styles': 'product.styles',
    '/core/products/colors': 'product.colors',
    '/core/products/genders': 'product.genders',

    // SKU ENDPOINTS
    '/core/skus': 'sku.sku_ids',

    // SIZE ENDPOINTS
    '/core/sizes': 'size.sizes',

    // GEOGRAPHIC ENDPOINTS
    '/core/locations': 'geographic.locations',
    '/core/locations/location-types': 'geographic.location_types',
    '/core/locations/regions': 'geographic.regions',
    '/core/locations/countries': 'geographic.countries',
    '/core/locations/territories': 'geographic.territories',
    '/core/locations/cities': 'geographic.cities',
    '/core/locations/budget-levels': 'geographic.budget_levels',

    // CATALOGUE ENDPOINTS
    '/core/catalogue/seasons': 'catalogue.seasons',
    '/core/catalogue/events': 'catalogue.events',

    // PRODUCT GROUP ENDPOINTS
    '/core/product-groups': 'product_groups.groups',

    // LOCATION CLUSTER ENDPOINTS
    '/core/location-clusters': 'location_cluster.location_clusters',

    // LOCATION GROUP ENDPOINTS
    '/core/location-groups': 'location_groups.groups',
    '/core/size-runs': 'size_run.size_runs',
  },

  // NON-FILTERABLE DIMENSION ATTRIBUTES
  /**
   * Non-filterable dimension attributes are those are not used to filter against any endpoint. You can map them to
   * synthetic `dimension.attribute` paths that will get merged into the final state shape. We expose this option to
   * streamline the synchronisation of filter state shape for the entire system.
   *
   * For example, the `product.product` path, while not used to filter against any endpoint, can still be incorporated
   * into the final state to track selections of products for components serving the `/core/products` endpoint.
   */
  nonFilterableMap: {
    '/core/catalogue/season-events': 'catalogue.season_events',
  },

  // CONFIGURATION
  /**
   * A configuration object requesting callbacks to derive unique identifiers for each filter option item based on
   * the expected shape of each `dimension.attribute` in the request.
   *
   * Non-filterable attribute will not have an expected request shape and so we just ask for a string identifier.
   */
  config: {
    // PRODUCT ENDPOINTS
    '/core/products': (item) => ({ id: item.id }),
    '/core/products/departments': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/sub-departments': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/seasons': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/collection-types': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/events': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/brands': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/season-events': (item) => ({
      season: { id: item.season.id, description: item.season.description },
      event: { id: item.event.id, description: item.event.description },
    }),
    '/core/products/budget-levels': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/classes': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/sub-classes': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/manufacturers': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/articles': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/materials': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/styles': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/colors': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/products/genders': (item) => ({
      id: item.id,
      description: item.description,
    }),

    // SKU ENDPOINTS
    '/core/skus': (item) => item.sku_id,

    // GEOGRAPHIC ENDPOINTS
    '/core/locations': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/location-types': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/regions': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/countries': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/territories': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/cities': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/locations/budget-levels': (item) => ({
      id: item.id,
      description: item.description,
    }),

    // CATALOGUE ENDPOINTS
    '/core/catalogue/seasons': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/catalogue/events': (item) => ({
      id: item.id,
      description: item.description,
    }),
    '/core/catalogue/season-events': (item) =>
      `${item.season.id}-${item.event.id}`,

    // PRODUCT GROUP ENDPOINTS
    '/core/product-groups': (item) => item.group,

    // LOCATION CLUSTER ENDPOINTS
    '/core/location-clusters': (item) => item.cluster_name,

    // LOCATION GROUP ENDPOINTS
    '/core/location-groups': (item) => item.group,

    // SIZE ENDPOINTS
    '/core/sizes': (item) => item.size_id,

    // SIZE RUN ENDPOINTS
    '/core/size-runs': (item) => item.size_run,
  },
});

export const {
  requestTransformers,
  responseTransformers,
  unsetNonFilterableDimensionAttributes,
  parseFrontendFilterOption,
  buildFrontendFilterOption,
} = transformers;

/**
 * Public lookup from a core filter endpoint to its `dimension.attribute` path
 * in `CoreFiltersFrontendState`. Merged from the filterable + non-filterable
 * maps so consumers don't have to know which side an endpoint lives in.
 */
export const endpointToDimensionAttribute = {
  ...transformers.__mappings.filterable,
  ...transformers.__mappings.nonFilterable,
};
